/**
 * Scope-aware entry point for the DocumentTemplate authoring UI.
 *
 * Shows a single auto-populated list of templates visible at the current
 * scope (system, office, program_manager, program). The list dedupes by
 * key and picks the most-specific leaf for display — so an office sees its
 * own customization of the system CNS rather than both.
 *
 * At program scope, the view splits into two lists:
 *   1. "Documents" — generated document instances (empty stub in V1)
 *   2. "Available document templates" — the visible template list
 *
 * A "New template" modal lets the user create a fresh template at this
 * scope. Editing an inherited template triggers a silent auto-customize on
 * first save (handled in the Editor).
 */
import { useEffect, useState } from 'react'
import { FileText, Plus } from 'lucide-react'
import Badge from '../ui/Badge'
import DocumentTemplateEditor from './DocumentTemplateEditor'
import NewTemplateModal from './NewTemplateModal'
import { useUser } from '../../contexts/UserContext'
import { listVisibleTemplates } from './api'
import { dedupeByKeyForScope } from './cascade'
import { OWNER_LEVEL_BADGE } from './types'
import type { DocumentTemplate, Scope } from './types'

interface Props {
  scope: Scope
  title?: string
  subtitle?: string
}

export default function DocumentTemplateManager({ scope, title, subtitle }: Props) {
  const { user } = useUser()
  const [visible, setVisible] = useState<DocumentTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  const isProgramScope = scope.level === 'program'
  const displayTitle = title || (isProgramScope ? 'Documents' : 'Document Templates')
  const displaySubtitle =
    subtitle ||
    (scope.level === 'system'
      ? 'System-wide document templates curated by Overmatch.'
      : scope.level === 'office'
      ? 'Document templates for your office. Inherited from system by default; edit any to customize.'
      : scope.level === 'program_manager'
      ? 'Your personal document templates, applied across every program you own.'
      : 'Document templates available for this program. Edit any to customize for this program only.')

  const refresh = async () => {
    setError(null)
    try {
      const templates = await listVisibleTemplates({
        scope_level: scope.level,
        office_id: user?.office?.id || null,
        user_id: user?.id || null,
        program_id: scope.level === 'program' ? (scope.ownerId || null) : null,
        include_drafts: true,
      })
      setVisible(dedupeByKeyForScope(templates, scope))
    } catch (err: any) {
      setError(err.message || 'Failed to load templates')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (user !== undefined) refresh()
  }, [scope.level, scope.ownerId, user?.id, user?.office?.id])

  if (selectedId) {
    return (
      <DocumentTemplateEditor
        templateId={selectedId}
        scope={scope}
        scopeContext={{ userId: user?.id, officeId: user?.office?.id }}
        onBack={() => {
          setSelectedId(null)
          refresh()
        }}
        onCustomized={(newId) => setSelectedId(newId)}
      />
    )
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{displayTitle}</h1>
          <p className="text-sm text-muted-foreground mt-1">{displaySubtitle}</p>
        </div>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md"
        >
          <Plus className="w-4 h-4" />
          New template
        </button>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive rounded-lg p-4 text-sm">
          {error}
        </div>
      )}

      {/* Program scope only: generated documents list on top (stub in V1). */}
      {isProgramScope && (
        <section className="space-y-3">
          <h2 className="text-lg font-medium text-foreground">Documents</h2>
          <div className="bg-card border border-dashed border-border rounded-lg p-6 text-sm text-muted-foreground">
            No documents have been generated yet. Generate one from a template below to promote it here.
          </div>
        </section>
      )}

      <section className="space-y-3">
        {isProgramScope && (
          <h2 className="text-lg font-medium text-foreground">Available document templates</h2>
        )}
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="text-sm text-muted-foreground italic">
            No templates visible at this scope yet.
          </div>
        ) : (
          <ul className="space-y-2">
            {visible.map((tpl) => {
              const badge = OWNER_LEVEL_BADGE[tpl.owner_level]
              return (
                <li key={tpl.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(tpl.id)}
                    className="w-full bg-card border border-border hover:border-primary/60 rounded-lg px-4 py-3 text-left transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-foreground">{tpl.name}</span>
                          <Badge variant={badge.variant}>{badge.label}</Badge>
                          {!tpl.is_active && <Badge variant="warning">Draft</Badge>}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 font-mono">{tpl.key}</div>
                        {tpl.description && (
                          <div className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {tpl.description}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {tpl.sections.length} section{tpl.sections.length === 1 ? '' : 's'}
                        {' · '}
                        {tpl.samples.length} sample{tpl.samples.length === 1 ? '' : 's'}
                      </div>
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <NewTemplateModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        scope={scope}
        onCreated={(tpl) => {
          setModalOpen(false)
          setSelectedId(tpl.id)
        }}
      />
    </div>
  )
}

export type { DocumentTemplate }
